import { describeImportSiteExecution } from '@/graphql/satellite-site'
import { BaseStore } from '@/store'
import { InjectionKey, onMounted, reactive } from 'vue'
import { Router, useRouter } from 'vue-router'

interface Show {
  loading: boolean
  result: boolean
}

interface DomainResult {
  domain: string
  progress: boolean
  succeed: boolean
  error?: string | null
}

interface Result {
  location: string
  total: number
  succeed: number
  errors: number
  domains: DomainResult[]
}

type Filter = 'all' | 'completed' | 'error'

interface State {
  id: string
  location: string
  show: Show
  filter: Filter
  result: Result
}

const state = reactive<State>({
  id: '',
  location: '',
  show: {
    loading: false,
    result: false,
  },
  filter: 'all',
  result: {
    location: '',
    total: 0,
    succeed: 0,
    errors: 0,
    domains: [],
  },
})

let router: Router
let reloadTimer: NodeJS.Timer
const reloadInterval = 30 * 1000

const setup = () => {
  router = useRouter()

  onMounted(async () => {
    await requestDescribeExecution()
  })
}

const requestDescribeExecution = async () => {
  if (reloadTimer) {
    clearInterval(reloadTimer)
  }

  state.show.loading = true
  const id = router.currentRoute.value.params.id as string
  if (state.id !== id) {
    state.show.result = false
  }

  state.id = id

  const result = await describeImportSiteExecution(id)
  const describe = result.data?.satellitesiteDescribeImportSiteExecution
  // console.log(describe)

  if (!describe || !describe.location || !describe.domains) {
    state.show.loading = false
    return
  }

  const domains = describe.domains.map((d) => {
    const errorType = d.results?.find((ret) => ret.error !== null)
    const noError = errorType === undefined
    const completed = noError
      ? d.results?.find((ret) => ret.type === 'cloudfront') !== undefined
      : true

    let error = errorType?.error
    switch (error) {
      case 'No such zone for cloudflare':
        error = 'ドメインが登録されていません。'
        break
      case 'Rate exceeded':
        error = '一時的に実行可能処理数を超えました。'
        break
      case 'One or more of the CNAMEs you provided are already associated with a different resource.':
        error = 'ドメインは使用できませんでした。'
        break
      case 'No such page resources':
        error = 'SCRSサーバーにドメインのテンプレートが見つかりませんでした。'
        break
      case 'No such domain directory':
        error = 'SCRSサーバーにドメインのテンプレートが見つかりませんでした。'
        break
    }

    return {
      domain: d.domain,
      progress: !completed,
      succeed: noError && completed,
      error,
    }
  })

  const total = describe.domains.length
  const succeed = domains.reduce((p, d) => (p += d.succeed ? 1 : 0), 0)
  const errors = domains.reduce((p, d) => (p += d.error ? 1 : 0), 0)

  state.result = {
    location: describe.location,
    total,
    succeed,
    errors,
    domains,
  }

  state.show.result = true
  state.show.loading = false

  if (total - succeed - errors > 0) {
    reloadTimer = setInterval(() => {
      reload()
    }, reloadInterval)
  }
}

const reload = async () => {
  await requestDescribeExecution()
}

const clickFilterAll = async () => {
  state.filter = 'all'
}

const clickFilterCompleted = async () => {
  state.filter = 'completed'
}

const clickFilterError = async () => {
  state.filter = 'error'
}

interface Store {
  state: State
  setup: () => void
  reload: () => void
  clickFilterAll: () => void
  clickFilterCompleted: () => void
  clickFilterError: () => void
}

type S = Readonly<Store>
const key: InjectionKey<S> = Symbol()
const store: BaseStore<S> = {
  key,
  state,
  setup,
  reload,
  clickFilterAll,
  clickFilterCompleted,
  clickFilterError,
}

export default store
